import { FC, ReactNode } from 'react';
import classNames from 'classnames';
import { Tooltip } from '../Tooltip';
import styles from './kpiInline.module.sass';

export type KpiDelta = 'up' | 'down' | 'flat';
export type KpiSize = 'md' | 'lg' | 'xl';

export interface KpiInlineProps {
  label: string;
  value: ReactNode;
  /** Small unit suffix appended after the value, e.g. "%" or "M" */
  suffix?: string;
  /** Delta indicator below the value */
  delta?: string;
  /** Sentiment of the delta (up = improvement / green, down = regression / red) */
  deltaType?: KpiDelta;
  /**
   * Multiple delta chips rendered as a row below the value. When provided,
   * overrides the single `delta`/`deltaType` pair. Each chip carries its own
   * sentiment color, useful for showing e.g. MoM and YoY side by side.
   */
  deltas?: Array<{ label: string; value: string; type?: KpiDelta }>;
  /** Secondary hint text under the delta line */
  hint?: ReactNode;
  /** Slot for a small sparkline rendered to the right of the label */
  trend?: ReactNode;
  /** Native tooltip on the label — surfaces a "what is this metric" definition on hover. */
  tooltip?: string;
  /** Value font size — md (18) default, lg (26) hero, xl (32) display */
  size?: KpiSize;
  /** Override the value color */
  color?: string;
  className?: string;
}

const DELTA_GLYPH: Record<KpiDelta, string> = {
  up: '↑',
  down: '↓',
  flat: '=',
};

/**
 * KpiInline — Glasstape inline KPI atom.
 * Use inside <ChartFrame kpi={…}/> or alone in a flex row.
 *
 * Spec source: /Glasstape/storybook/stories-dataviz.jsx :: KpiInline
 */
export const KpiInline: FC<KpiInlineProps> = ({
  label,
  value,
  suffix,
  delta,
  deltaType = 'flat',
  deltas,
  hint,
  trend,
  tooltip,
  size = 'md',
  color,
  className,
}) => {
  return (
    <div className={classNames(styles.atom, size === 'lg' && styles.lg, size === 'xl' && styles.xl, className)}>
      <div className={styles.cellHead}>
        {tooltip ? (
          <Tooltip content={tooltip}>
            <span className={styles.label}>{label}</span>
          </Tooltip>
        ) : (
          <span className={styles.label}>{label}</span>
        )}
        {trend && <span className={styles.spark}>{trend}</span>}
      </div>
      <div className={styles.value} style={color ? { color } : undefined}>
        {value}
        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </div>
      {deltas && deltas.length > 0 ? (
        <div className={styles.deltaRow}>
          {deltas.map((d) => {
            const t = d.type ?? 'flat';
            return (
              <span key={d.label} className={classNames(styles.delta, styles[t])}>
                <span className={styles.deltaLabel}>{d.label}</span>
                <span aria-hidden="true">{DELTA_GLYPH[t]}</span>
                {d.value}
              </span>
            );
          })}
        </div>
      ) : delta ? (
        <span className={classNames(styles.delta, styles[deltaType])}>
          <span aria-hidden="true">{DELTA_GLYPH[deltaType]}</span>
          {delta}
        </span>
      ) : null}
      {hint && <span className={styles.hint}>{hint}</span>}
    </div>
  );
};

KpiInline.displayName = 'KpiInline';

// ─── KpiStrip ────────────────────────────────────────────────────
// Full-width row of KPI cells separated by hairline borders.
// Spec source: /Glasstape/storybook/stories-abs-* :: KpiStrip
export interface KpiStripProps {
  items: KpiInlineProps[];
  className?: string;
}

export const KpiStrip: FC<KpiStripProps> = ({ items, className }) => {
  return (
    <div className={classNames('gt-scope', styles.strip, className)}>
      {items.map((item) => (
        <div key={item.label} className={styles.cell}>
          <KpiInline {...item} />
        </div>
      ))}
    </div>
  );
};

KpiStrip.displayName = 'KpiStrip';

export default KpiInline;
